/**
 * Timing for the waiting screen's poll.
 *
 * A slow FLOW backend used to cost the customer their signing: a fixed
 * two-second poll gave up after a minute and sent them to the yellow state,
 * which means manual handling. The budget here is long enough to outlast a
 * struggling backend, and the delay grows so a long wait does not turn into
 * hundreds of requests against the thing that is already slow.
 */

/** How long of reachable, empty-task polls before the yellow state. */
export const POLL_BUDGET_MS = 300_000;

/** A hung request is aborted so it cannot stall the waiting screen. */
export const POLL_REQUEST_TIMEOUT_MS = 20_000;

const NETWORK_FETCH_MESSAGE =
	/failed to fetch|networkerror|load failed|network request failed|internet connection appears to be offline|fetch failed/i;

export type HealthyPollBudget = {
	accumulatedMs: number;
	lastHealthyAt: number | null;
};

export const emptyHealthyPollBudget = (): HealthyPollBudget => ({
	accumulatedMs: 0,
	lastHealthyAt: null,
});

/** Adds the gap since the last reachable empty poll. A pause in between is not counted. */
export const recordHealthyPoll = (
	budget: HealthyPollBudget,
	now: number
): HealthyPollBudget => ({
	accumulatedMs:
		budget.lastHealthyAt === null
			? budget.accumulatedMs
			: budget.accumulatedMs + (now - budget.lastHealthyAt),
	lastHealthyAt: now,
});

export const pauseHealthyPollBudget = (
	budget: HealthyPollBudget
): HealthyPollBudget => ({
	accumulatedMs: budget.accumulatedMs,
	lastHealthyAt: null,
});

export const isHealthyPollBudgetExhausted = (
	budget: HealthyPollBudget
): boolean => budget.accumulatedMs >= POLL_BUDGET_MS;

export const isBrowserReadyToPoll = ({
	visible,
	online,
}: {
	visible: boolean;
	online: boolean;
}): boolean => visible && online;

export const isPollNetworkFailure = (error: unknown): boolean => {
	if (!(error instanceof Error)) return false;
	if (error.name === "AbortError" || error.name === "TimeoutError") {
		return true;
	}
	if (error.name === "NetworkError") return true;
	return (
		error.name === "TypeError" && NETWORK_FETCH_MESSAGE.test(error.message)
	);
};

/**
 * Delay before the next attempt. Stays tight while a normal response is still
 * plausible, then backs off.
 */
export const pollDelayForAttempt = (attempt: number): number => {
	if (attempt < 5) return 2_000;
	if (attempt < 11) return 5_000;
	return 10_000;
};

/** How long the tasks poll may keep failing before the error page. */
export const UNAVAILABLE_BUDGET_MS = 300_000;

/** Failed polls are budgeted like healthy ones: an OK response resets it, a pause is not counted. */
export type FailedPollBudget = HealthyPollBudget;
export const emptyFailedPollBudget = emptyHealthyPollBudget;
export const recordFailedPoll = recordHealthyPoll;
export const pauseFailedPollBudget = pauseHealthyPollBudget;

export const isFailedPollBudgetExhausted = (
	budget: FailedPollBudget
): boolean => budget.accumulatedMs >= UNAVAILABLE_BUDGET_MS;

/** Which reassurance the waiting screen shows. */
export type WaitingPhase =
	"normal" | "slow" | "verySlow" | "offline" | "unavailable";

/**
 * The copy has to stop promising "a few seconds" once that is no longer true,
 * and has to say the wait is safe once it is clearly long.
 */
export const waitingPhaseForElapsed = (elapsedMs: number): WaitingPhase => {
	if (elapsedMs < 15_000) return "normal";
	if (elapsedMs < 90_000) return "slow";
	return "verySlow";
};

/** The customer's connection comes first, then ours, then the length of the wait. */
export const waitingPhaseFor = ({
	elapsedMs,
	online,
	unavailable = false,
}: {
	elapsedMs: number;
	online: boolean;
	unavailable?: boolean;
}): WaitingPhase => {
	if (!online) return "offline";
	if (unavailable) return "unavailable";
	return waitingPhaseForElapsed(elapsedMs);
};
